import { createAvailableFileElement, elements, removeAvailableFileElement, splitFilename } from './elements'
import { Filename, FileSettingsType, InsertTableSettings, Table, TableId, TableSettings, TablesInFile } from './types'

const title = 'test title'

export type FileVersion = {
    filename: Filename
    version: number
    createdAt: Date
}

export type FileVersions = Record<Filename, Array<FileVersion>> 
export enum LS_KEYS {
    authKeyKey = `AuthKeyKey`,
    projectIdKey = `ProjectIdKey`,
    docTypeKey = `DocTypeKey`,
    useCacheKey = `UseCacheKey`,
    lsKey = `Settings_${title}`,
    lastUploadedFiles = `LastUploadedFiles`,
    availableFiles = `AvailableFiles`,
    fileVersions = `FileVersions`,
    originalFiles = `OriginalFiles`,
    fileSettings = `FileSettings`,
    useAI = `UseAIKey`,
}

export type LS_TYPES = {
    [LS_KEYS.availableFiles]: {value: Array<Filename>}
    [LS_KEYS.lastUploadedFiles]: Array<Filename>
    tableId: Partial<InsertTableSettings>
    filename: TablesInFile
    [LS_KEYS.authKeyKey]: {value: string}
    [LS_KEYS.projectIdKey]: {value: string}
    [LS_KEYS.docTypeKey]: {value: number, selected: string}
    [LS_KEYS.useCacheKey]: {value: boolean}
    [LS_KEYS.lsKey]: Record<string, any>
    [LS_KEYS.fileVersions]: FileVersions
    [LS_KEYS.originalFiles]: Record<Filename, FileVersion>
    [LS_KEYS.fileSettings]: FileSettingsType
    [LS_KEYS.useAI]: {value: boolean}
}

let pk = ''
try {
    // @ts-ignore
    pk = Office.context.partitionKey ?? ''
} catch(err) {
}

export function pushLS<T extends keyof LS_TYPES>(key: T, v: LS_TYPES[T]) {
    localStorage.setItem(pk + key, JSON.stringify(v))
}

export function getLS<T extends keyof LS_TYPES>(key: T, defaultValue: string = '{}'): LS_TYPES[T] {
    return JSON.parse(localStorage.getItem(pk + key) ?? defaultValue)
}

export const getToken = () => {
    const value = getLS(LS_KEYS.authKeyKey).value 
    if (!value)
        throw new Error('Forbidden')
    return value
}

export const isAIAvailable = () => {
    return getLS(LS_KEYS.useAI).value ?? false
}
export const updateIsAI = (value: boolean) => {
    pushLS(LS_KEYS.useAI, {value})
}

export const getFileSettings = (filename: Filename) => {
    return getLS(filename as LS_KEYS.fileSettings)
}

export const saveFileSettings = (filename: Filename, settings: FileSettingsType) => {
    return pushLS(filename as LS_KEYS.fileSettings, settings)
}

export const resetSavedFiles = () => {
    const files = getAvailableFiles()
    deleteFiles(files)
    pushLS(LS_KEYS.originalFiles, {})
    pushLS(LS_KEYS.fileVersions, {})
    pushLS(LS_KEYS.lastUploadedFiles, [])
}

export const getProjectId = () => getLS(LS_KEYS.projectIdKey).value ?? ''

export const saveSettings = () => {
    if (elements.authKey)
        pushLS(LS_KEYS.authKeyKey, { value: elements.authKey.value })
    if (elements.projectId)
        pushLS(LS_KEYS.projectIdKey, { value: elements.projectId.value })
    if (elements.useCache)
        pushLS(LS_KEYS.useCacheKey, { value: elements.useCache.checked })
    if (elements.docType)
        pushLS(LS_KEYS.docTypeKey, {
            value: elements.docType.selectedIndex,
            selected: elements.docType.selectedOptions[0]?.value,
        })
}

export const saveFileVersions = (filename: Filename, file: FileVersion, add: boolean) => {
    const current = getFileVersions(filename)
    if (add)
        current.push(file)
    else {
        const found = current.findIndex(c => c.version == file.version)
        if (found >= 0) current.splice(found, 1)
    }
    const all = getAllFileVersions()
    if (current.length)
        all[filename] = current
    else
        delete all[filename]
    pushLS(LS_KEYS.fileVersions, all)
    const p = getOriginalFilenames()
    if (add)
        p[file.filename] = {
            ...file,
            filename,
        }
    else
        delete p[file.filename]
    pushLS(LS_KEYS.originalFiles, p)
}

export const getOriginalFilenames = () => getLS(LS_KEYS.originalFiles)
export const getAllFileVersions = () => getLS(LS_KEYS.fileVersions)
export const getFileVersions = (filename: Filename) => getAllFileVersions()[filename] ?? []

export const COPY_FILE = (filename: Filename, tables: TablesInFile) => {
    const wp = `${tables.strings.project}: ${filename}` as Filename
    const fn = createFileInstance(wp)
    addFile(fn, tables)
    createAvailableFileElement(fn)
    return fn
}
export const createFileInstance = (filename: Filename) => {
    const current = getFileVersions(filename)
    const version = current.length ? current.at(-1)!.version + 1 : 1
    const newFilename = version > 1 ? `${filename}, v${version}` as Filename : filename
    saveFileVersions(filename, {
        createdAt: new Date(),
        filename: newFilename,
        version,
    }, true)
    return newFilename
}

export const getTablesByFN = (filename: Filename) => `tables_${filename}` as Filename & LS_KEYS

export const getSettingByTableId = (tableId: TableId) => `settings_${tableId}` as TableId & LS_KEYS

export const getTableSettings = (tableId: TableId) => {
    return getLS(getSettingByTableId(tableId) as unknown as 'tableId')
}

export const saveTableSettings = (tableId: TableId, settings: Partial<InsertTableSettings>) => {
    pushLS(getSettingByTableId(tableId) as unknown as 'tableId', settings)
}

export const addFile = (filename: Filename, a: TablesInFile) => {
    pushLS(getTablesByFN(filename) as unknown as 'filename' , a)
    addAvailableFiles([filename])
}

export const getFile = (filename: Filename) => {
    return getLS(getTablesByFN(filename) as unknown as 'filename')
}

export const getLastUploadedFiles = () => {
    return getLS(LS_KEYS.lastUploadedFiles, '[]')
}

export const updateLastUploadedFiles = (files: Array<Filename>) => {
    pushLS(LS_KEYS.lastUploadedFiles, files)
}

export const getAvailableFiles = () => {
    return getLS(LS_KEYS.availableFiles).value || []
}
export const addAvailableFiles = (files: Array<Filename>) => {
    const available = getAvailableFiles()
    const ret = [...new Set([...files, ...available])]
    ret.sort((a, b) => a < b ? -1 : 1)
    pushLS(LS_KEYS.availableFiles, {value: ret})
}

export const getTableId = (filename: Filename, page: number, index: number) => {
    return `table_${filename}_${page}_${index}` as TableId
}

export const getFileId = (project: string, filename: Filename) => {
    return `Project_${project}_file_${filename}` as Filename
}

export const deleteFiles = (files: Array<Filename>) => {
    removeAvailableFiles(files)
    for (const file of files)
        removeAvailableFileElement(file)
}

export const removeAvailableFiles = (files: Array<Filename>) => {
    const available = new Set(getAvailableFiles())
    const of = getOriginalFilenames()
    for (const file of files) {
        const project = splitFilename(file).project
        const originals = of[file]
        if (originals) {
            saveFileVersions(originals.filename, {
                ...originals,
                filename: file,
            }, false)
            delete of[file]
        } else {
            saveFileVersions(file, {
                version: 1,
                filename: file,
                createdAt: new Date(),
            }, false)
        }
        available.delete(file)
        const fn = getFile(file)
        if (fn?.tables)
            for (let page = 0; page < fn.tables.length; ++page)
                for (let index = 0; index < fn.tables[page].length; ++index)
                    localStorage.removeItem(getSettingByTableId(getTableId(file, page, index)))
        localStorage.removeItem(getTablesByFN(file))
        localStorage.removeItem(getFileId(project, file))
    }
    pushLS(LS_KEYS.availableFiles, {value: [...available]})
    pushLS(LS_KEYS.originalFiles, of)
}
